import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldPlus, DownloadSimple, IdentificationCard } from "@phosphor-icons/react";
import { Logo, Copyright } from "@/components/insurehub/Logo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

function errText(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
}

async function downloadBlob(payload) {
  const res = await axios.post(`${API}/public/ecard/download`, payload, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = "e-health-card.png"; a.click();
  window.URL.revokeObjectURL(url);
}

export default function PublicEcard() {
  const [mode, setMode] = useState("mobile"); // 'mobile' | 'code'
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [empCode, setEmpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestOtp = async () => {
    if (mobile.replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit mobile number."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/ecard/otp/request`, { mobile: mobile.trim() });
      setOtpSent(true);
      if (data.demo_code) toast.success(`Demo mode — your code is ${data.demo_code}`);
      else toast.success(`OTP sent to ${data.masked_email}`);
    } catch (err) { toast.error(errText(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadWithOtp = async () => {
    if (!code.trim()) { toast.error("Enter the OTP code."); return; }
    setBusy(true);
    try { await downloadBlob({ mobile: mobile.trim(), code: code.trim() }); toast.success("E-card downloaded!"); }
    catch (err) {
      const d = err.response?.data;
      toast.error(d && d.type !== "image/png" ? "Invalid or expired code." : "Download failed.");
    } finally { setBusy(false); }
  };

  const downloadWithCode = async () => {
    if (!empCode.trim()) { toast.error("Enter your employee code."); return; }
    setBusy(true);
    try { await downloadBlob({ employee_code: empCode.trim() }); toast.success("E-card downloaded!"); }
    catch (err) { toast.error("No member found for that employee code."); }
    finally { setBusy(false); }
  };

  const inputCls = "h-11 w-full rounded-lg border border-zinc-300 px-3 text-sm focus:border-emerald-500 focus:outline-none";
  const tab = (k, label) => (
    <button data-testid={`ecard-tab-${k}`} onClick={() => setMode(k)}
      className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${mode === k ? "bg-emerald-900 text-white" : "bg-white text-zinc-600 border border-zinc-200"}`}>
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center"><Logo size={38} textClass="text-lg" /></div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-7 shadow-[0_8px_30px_rgba(0,0,0,0.06)]" data-testid="public-ecard-card">
          <div className="flex items-center gap-2 text-emerald-700">
            <IdentificationCard size={26} weight="duotone" />
            <h1 className="font-heading text-xl font-semibold text-emerald-950">Download your e-health card</h1>
          </div>
          <p className="mt-1 text-sm text-zinc-500">No login needed. Verify with your mobile (OTP) or your employee code.</p>

          <div className="mt-5 flex gap-2">{tab("mobile", "Mobile + OTP")}{tab("code", "Employee code")}</div>

          {mode === "mobile" ? (
            <div className="mt-5 space-y-3">
              <input data-testid="ecard-mobile" className={inputCls} placeholder="Registered mobile number"
                value={mobile} onChange={(e) => setMobile(e.target.value)} disabled={otpSent} />
              {!otpSent ? (
                <button data-testid="ecard-send-otp" onClick={requestOtp} disabled={busy}
                  className="w-full h-11 rounded-lg bg-emerald-900 hover:bg-emerald-800 text-white font-medium transition-colors disabled:opacity-60">
                  {busy ? "Sending…" : "Send OTP"}
                </button>
              ) : (
                <>
                  <input data-testid="ecard-otp" className={inputCls} placeholder="Enter 6-digit OTP"
                    value={code} onChange={(e) => setCode(e.target.value)} />
                  <button data-testid="ecard-download-otp" onClick={downloadWithOtp} disabled={busy}
                    className="w-full h-11 rounded-lg bg-emerald-900 hover:bg-emerald-800 text-white font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                    <DownloadSimple size={18} /> {busy ? "Verifying…" : "Verify & download"}
                  </button>
                  <button onClick={() => { setOtpSent(false); setCode(""); }} className="w-full text-xs text-zinc-500 hover:underline">Change mobile number</button>
                </>
              )}
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              <input data-testid="ecard-empcode" className={inputCls} placeholder="Employee code e.g. EMP1001"
                value={empCode} onChange={(e) => setEmpCode(e.target.value)} />
              <button data-testid="ecard-download-code" onClick={downloadWithCode} disabled={busy}
                className="w-full h-11 rounded-lg bg-emerald-900 hover:bg-emerald-800 text-white font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                <DownloadSimple size={18} /> {busy ? "Fetching…" : "Download e-card"}
              </button>
            </div>
          )}
        </div>
      </main>
      <footer className="border-t border-zinc-200 bg-white py-5 text-center"><Copyright /></footer>
    </div>
  );
}
